import { Component, computed, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { SkeletonModule } from "primeng/skeleton";
import { NetworkStatusService } from "../../../../../shared/services/network-status.service";
import { DividerModule } from "primeng/divider";
import { OpenItemSelection } from "../open-items-list/open-items.component";
import { IssueItemComponent } from "./components/issue-item/issue-item.component";
import { JobIssueVendor, Media } from "../../../../../core/database/db";
import { AuthService } from "../../../../../core/authentication/auth.service";
import { LoggerService } from "../../../../../shared/services/logger.service";
import { ImageHydrationService } from "../../../../../core/sync/image-sync/image-hydration.service";
import { BlobService } from "../../../../../shared/services/blob.service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";
import { OpenItemsDataService } from "../services/open-items-data.service";
import { JobIssueWithDetails } from "../../../../../domains/jobIssues/models/JobIssueWithDetails";
import { BqaImageItem } from "../../../../../domains/media/components/images-modal/bqa-image-item";

export type PrintableMedia = Pick<Media, "parentIdentifier" | "fileIdentifier" | "base64Image" | "isThumbnailOnly"> & {
  blob?: Blob;
};

@Component({
  selector: "app-print-items",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SkeletonModule,
    DividerModule,
    IssueItemComponent
  ],
  templateUrl: "./print-items.component.html",
  styleUrls: ["./print-items.component.scss"]
})

export class PrintItemsComponent implements OnInit {
  lotId: number = 0;
  communityId: string | null = null;
  isOnline: boolean = true;
  headerTitle: string = "Open Items";
  headerSubtitle = signal<string>("");
  issuesToPrint = signal<JobIssueWithDetails[]>([]);

  vendorAssignments = signal<Map<string, JobIssueVendor[]>>(new Map());
  openItemsSelectionType = signal<OpenItemSelection>(OpenItemSelection.Room);
  totalNumberOfIssues = signal<number>(0);

  CMName: string = "";
  today = Date.now();
  isLoadingFullImages: boolean = false;
  private blobUrls: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: ExtendedRouter,
    private networkService: NetworkStatusService,
    private authService: AuthService,
    private logger: LoggerService,
    private imageHydrationService: ImageHydrationService,
    private blobService: BlobService,
    private toastService: UiToastService,
    protected openItemsDataService: OpenItemsDataService
  ) {
    if (this.authService.currentAccount?.name) {
      this.CMName = this.authService.currentAccount.name;
    }
    this.networkService.online$.pipe(takeUntilDestroyed()).subscribe(val => {
      this.isOnline = val;
    });

    this.issuesToPrint.set(this.router.getCurrentNavigation()?.extras.state?.["selectedIssues"] ?? []);
    this.openItemsSelectionType.set(this.router.getCurrentNavigation()?.extras.state?.["openItemsSelectionType"] ?? OpenItemSelection.Room);
    this.totalNumberOfIssues.set(this.router.getCurrentNavigation()?.extras.state?.["totalNumberOfIssues"] ?? this.issuesToPrint().length);
  }

  lotBlock = computed(() => this.openItemsDataService.lot()?.lotBlock ?? "");
  lotAddress = computed(() => this.openItemsDataService.lot()?.streetAddress ?? "");
  date = computed(() => new Date());
  media = computed(() => (this.openItemsDataService.media() ?? [])
    .filter(img => !img.deleted && (img.base64Image || img.resourceUri))
  );
  mediaMap = signal<Map<string, BqaImageItem[]>>(new Map());

  async ngOnInit(): Promise<void> {
    this.lotId = Number(this.route.parent?.snapshot.paramMap.get("lotId"));
    this.communityId = this.route.parent?.snapshot.paramMap.get("communityId") ?? null;

    if (this.issuesToPrint().length === 0) {
      this.goBack();
    } else {
      const isShowingThumbnails = this.media().some(media => media.isThumbnailOnly);

      if (isShowingThumbnails && this.isOnline) {
        try {
          this.logger.info("[PrintItemsComponent] Thumbnails are being displayed. Loading full images for print view.");
          this.isLoadingFullImages = true;

          const mediaIdentifiers = this.media().map(media => media.fileIdentifier);

          const fullImages = (await this.imageHydrationService.getFullImagesForFileIdentifiers(mediaIdentifiers));
          const fullImagesWithBase64Blobs = await Promise.all(fullImages.map(async fullImageData => {
            const base64Image = await this.blobService.blobToBase64(fullImageData.image, this.logger);
            return { ...fullImageData, base64Image };
          }));

          const mediaToPrint = this.media().map(media => {
            const printableMedia: PrintableMedia = {
              fileIdentifier: media.fileIdentifier,
              parentIdentifier: media.parentIdentifier,
              base64Image: media.base64Image,
              isThumbnailOnly: media.isThumbnailOnly || false
            };

            const fullImage = fullImagesWithBase64Blobs.find(fullImage => fullImage.fileIdentifier === media.fileIdentifier);
            if (fullImage) {
              printableMedia.base64Image = fullImage.base64Image;
              printableMedia.blob = fullImage.image;
              printableMedia.isThumbnailOnly = false;
            }

            return printableMedia;
          });

          for (const issue of this.issuesToPrint()) {
            const mediaForIssue = mediaToPrint.filter(media => media.parentIdentifier === issue.identifier);
            const printableMedia = this.convertToPrintableMedia(mediaForIssue);
            this.mediaMap().set(issue.identifier, printableMedia);
          }

          this.isLoadingFullImages = false;
        } catch (err) {
          this.logger.error("[PrintItemsComponent] Error loading full images for print view", err);
          this.isLoadingFullImages = false;
        }
      } else if (this.isOnline === false) {
        this.logger.info("[PrintItemsComponent] User is offline. Full images may not be available for print view.");

        this.isLoadingFullImages = false;
        for (const issue of this.issuesToPrint()) {
          const mediaToPrint = this.media().map<PrintableMedia>(media => {
            return {
              fileIdentifier: media.fileIdentifier,
              parentIdentifier: media.parentIdentifier,
              base64Image: media.base64Image,
              isThumbnailOnly: media.isThumbnailOnly || false
            };
          });
          const mediaForIssue = mediaToPrint.filter(media => media.parentIdentifier === issue.identifier);
          const printableMedia = this.convertToPrintableMedia(mediaForIssue);
          this.mediaMap().set(issue.identifier, printableMedia);
        }

        this.toastService.warn("You are currently offline. Low resolution images will be used for print view.");
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrls();
  }

  goBack(): void {
    try {
      this.router.navigateWithPersistedState(["/cm/all-my-lots/community", this.communityId ?? 0, "lot", this.lotId, "open-items"]);
    } catch (err) {
      this.logger.error(err);
    }
  }

  onPrint(): void {
    setTimeout(() => {
      window.print();
    }, 0);
  }

  onImageError(): void {
    this.logger.warn("An image failed to load in the print view. This could be due to a network issue or a problem with the image file.");
    this.toastService.warn("An image failed to load");
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);
    return url;
  }

  private cleanupBlobUrls(): void {
    this.blobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        this.logger.error(`Failed to revoke blob URL: ${url}`, e);
      }
    });
    this.blobUrls = [];
  }

  private convertToPrintableMedia(media: PrintableMedia[]): BqaImageItem[] {
    return media
      .map(image => {
        let src = "";

        if (image.blob) {
          src = this.createObjectUrlAndTrack(image.blob);
        } else if (image.base64Image) {
          const blob = this.blobService.base64ToBlob(image.base64Image);
          src = this.createObjectUrlAndTrack(blob);
        }

        return new BqaImageItem({
          azureBlobUri: src,
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      });
  }
}